import { computed, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { CanActivateFn, Router, UrlTree } from '@angular/router'

import { Observable, map } from 'rxjs'

import { PermissionValue } from '@flowx/designer/modules/workspaces/shared/roles'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { AiAvailabilityService } from '@flowx/designer/shared/services/ai-availability.service'

import { ensureOrganisationAdminWorkspaceSelected } from './set-organisation-admin-workspace.guard'

export function DefaultOrganisationAdminPermissionsGuard(
  permissions: PermissionValue[]
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router)
    const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

    // Every route guarded here lives under `/organisation-admin`, so org-admin is the context by
    // construction. Re-assert it: the parent route's canActivate runs only on activation, so a
    // sibling page that switched the selected workspace would otherwise make every later
    // navigation inside the subtree fail the check below and land on /403 (CORE-5339).
    ensureOrganisationAdminWorkspaceSelected(workspacesAndPermissionsStore)

    const isPermitted = computed(() => {
      const selectedWorkspace = workspacesAndPermissionsStore.selectedWorkspace()
      const userOrganisationPermissions =
        workspacesAndPermissionsStore.userOrganisationPermissions()
      if (!selectedWorkspace || !permissions) {
        return false
      }
      return (
        // Always true once the re-assertion above has run; kept as a defensive check in case
        // this guard is ever reused outside the `/organisation-admin` subtree.
        selectedWorkspace?.id === 'organisation-admin' &&
        permissions.some((permission) => userOrganisationPermissions[permission])
      )
    })

    return toObservable(isPermitted).pipe(
      map((isPermitted) => (isPermitted ? true : router.parseUrl('/403')))
    )
  }
}

// AI Settings pages are reachable only when at least one AI capability group
// (developer agents / runtime agents) is deployed in the environment
export function AiSettingsAvailabilityGuard(): CanActivateFn {
  return (): boolean | UrlTree => {
    const router = inject(Router)
    const aiAvailability = inject(AiAvailabilityService)

    return aiAvailability.anyAiAvailable() ? true : router.parseUrl('/403')
  }
}
